/**
 * Wire contract for the Reconciliations Agent. SPEC-0004 §Data.
 *
 * The conversation lives in the browser tab and is posted in full on every
 * turn — the server holds no session state (SPEC-0004 §2.1). That makes the
 * request body attacker-controlled in both *shape* and *size*, so the caps
 * below are a cost and injection-surface control rather than a UX preference.
 * An unbounded history is an unbounded bill, and ADR-0014 accepts that this is
 * the first per-request variable cost in the product.
 */
import { z } from 'zod';
/**
 * Only the two roles the client can legitimately author.
 *
 * Tool results are deliberately NOT representable here. They are produced
 * server-side inside the loop and never round-trip through the browser: a
 * client that could post a `tool` message could fabricate a finding and have
 * the model repeat the number as fact.
 */
export const agentRoleSchema = z.enum(['user', 'assistant']);
/** One turn of conversation. */
export const agentMessageSchema = z.object({
    role: agentRoleSchema,
    content: z.string().trim().min(1).max(8_000),
});
export const MAX_HISTORY_MESSAGES = 40;
export const agentChatRequestSchema = z.object({
    /** Full history, oldest first, ending with the new user message. */
    messages: z.array(agentMessageSchema).min(1).max(MAX_HISTORY_MESSAGES),
});
/**
 * Starter prompts, shown only while the conversation is empty. SPEC-0004 §3.
 *
 * They live here rather than in the panel so any second surface (an empty-state
 * card on the Dashboard, say) cannot drift from this list. Each one is chosen
 * to exercise a different tool path, so they double as the manual smoke test
 * in SPEC-0004 §Verification.
 */
export const AGENT_SUGGESTED_PROMPTS = [
    'What should I look at first this week?',
    'Which client has the most money at risk?',
    'Explain the highest-severity finding in my latest run.',
    'Are any roles failing to match between the two files?',
    'How did my last run compare to the one before it?',
    'Which findings have an estimated rate rather than a real one?',
];
//# sourceMappingURL=agent.js.map